import Constants from "../params/constants";
import type { FormattedExecutionResult } from "graphql";
import type { Dashboard, LogRow } from "../params/types";

type Unsubscribe = () => void;

const DASHBOARD_FIELDS = `
  process {
    running pid started_at status last_exit_code running_count
    rotation_active rotation_batch_index rotation_batch_count
    rotation_next_at rotation_batch_size rotation_shift_minutes
    rotation_wave
  }
  accounts {
    username password ig_username first_name last_name city country model_id has_password proxy proxy_label proxy_exit_ip has_proxy has_session
    enabled paused ai_paused warmup_enabled session_status last_error last_login_at
    known_followers_count followers_seeded followers_new_today replied_threads_count
    conversation_count last_activity_at pending_outbound_count
    messages_total replies_last_day
    sends_last_hour sends_last_day failed_outbound_count unread_count
    login_count_total login_count_today ig_follower_count avatar_url
    watcher_status last_inbox_tick_at last_followers_tick_at last_requests_tick_at
    progress_detail
    inbox_tab_ok followers_tab_ok requests_tab_ok
    last_inbox_latency_ms last_followers_latency_ms last_requests_latency_ms
    watcher_sends_ok watcher_sends_fail last_ban_at
    timezone locale process_running process_pid
    ai {
      response_mode delay_min_minutes delay_max_minutes provider model
      api_key has_api_key temperature system_prompt message_rule_prompt
      welcome_prompt welcome_template welcome_use_ai
      last_msg_activated last_msg_limit last_msg_prompt final_message_enabled final_message_after_count final_message_idle_hours final_message_template link_pool
      voice_id voice_api_key has_voice_api_key voice_replies_enabled
      poll_interval_min_ms poll_interval_max_ms
      max_threads_per_cycle_min max_threads_per_cycle_max
      max_threads_per_day_min max_threads_per_day_max max_messages_per_thread_per_day
      min_reply_gap_min_ms min_reply_gap_max_ms
      followers_enabled followers_follow_back followers_schedule
      followers_max_per_cycle_min followers_max_per_cycle_max
      followers_scan_limit_min followers_scan_limit_max
      quiet_hours_enabled quiet_hours_start quiet_hours_end quiet_hours_tz
      ignore_keywords blocklist_usernames skip_group_threads
      media_replies_enabled auto_accept_message_requests
      browser_mode browser_engine visible_browser ws_wake_enabled
      ws_wake_delay_min_sec ws_wake_delay_max_sec
      gallery_categories { id label }
    }
  }
  logs { at line account level }
  challenge { pending id account type channel destination message requested_at code_submitted expires_at }
  config {
    max_concurrent_sessions
    rotation_enabled
    rotation_batch_size
    rotation_shift_minutes
    hide_suspended_accounts
    browser_max_width
    browser_max_height
    blocklist_usernames
  }
  health {
    sends_last_hour sends_last_day failed_last_day failed_last_hour pending_outbound
    accounts_total accounts_enabled accounts_paused accounts_need_login
    accounts_banned
    quiet_hours_active
    challenge_age_sec warnings
  }
`;

interface LiveDashboardSubscription {
  subscription: Unsubscribe | null;
  run: (
    callback: (dashboard: Dashboard) => void,
    onError?: (err: unknown) => void,
  ) => Promise<Unsubscribe | null | undefined>;
  end: () => void;
}

export const liveDashboard: LiveDashboardSubscription = {
  subscription: null,
  run: async (callback, onError) => {
    return await new Promise((resolve, reject) => {
      liveDashboard.end();
      if (!Constants.graphqlWSClient) return resolve(undefined);

      liveDashboard.subscription = Constants.graphqlWSClient.subscribe(
        {
          query: `
            subscription DashboardLive {
              dashboard {
                ${DASHBOARD_FIELDS}
              }
            }
          `,
          variables: {},
        },
        {
          next: (res: FormattedExecutionResult<{ dashboard?: Dashboard }>) => {
            if (res?.errors?.length && onError) onError(res.errors[0]);
            if (res?.data?.dashboard) {
              callback(res.data.dashboard);
            }
          },
          error: (err: unknown) => {
            if (onError) onError(err);
            else reject(err);
          },
          complete: () => resolve(liveDashboard.subscription),
        },
      );
      resolve(liveDashboard.subscription);
    });
  },
  end: () => {
    if (liveDashboard.subscription) {
      liveDashboard.subscription();
      liveDashboard.subscription = null;
    }
  },
};

interface LiveLogSubscription {
  subscription: Unsubscribe | null;
  account: string | null;
  run: (
    account: string | null,
    callback: (row: LogRow) => void,
    onError?: (err: unknown) => void,
  ) => Promise<Unsubscribe | null | undefined>;
  end: () => void;
}

export const liveLog: LiveLogSubscription = {
  subscription: null,
  account: null,
  run: async (account, callback, onError) => {
    return await new Promise((resolve, reject) => {
      liveLog.end();
      if (!Constants.graphqlWSClient) return resolve(undefined);

      const key = account ? String(account).replace(/^@/, "").trim() : null;
      liveLog.account = key;

      liveLog.subscription = Constants.graphqlWSClient.subscribe(
        {
          query: `
            subscription LogLive($account: String) {
              log(account: $account) {
                at line account level
              }
            }
          `,
          variables: { account: key || null },
        },
        {
          next: (res: FormattedExecutionResult<{ log?: LogRow }>) => {
            if (res?.errors?.length && onError) onError(res.errors[0]);
            if (res?.data?.log) callback(res.data.log);
          },
          error: (err: unknown) => {
            if (onError) onError(err);
            else reject(err);
          },
          complete: () => resolve(liveLog.subscription),
        },
      );
      resolve(liveLog.subscription);
    });
  },
  end: () => {
    if (liveLog.subscription) {
      liveLog.subscription();
      liveLog.subscription = null;
      liveLog.account = null;
    }
  },
};
